import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { useDispatch } from 'react-redux';
import { showErrorToastMessage, showSuccessToastMessage } from '../Toasts/Toasts';
import * as actions from '../../Redux/Actions/index';

interface IReassignmentModalProps {
  show: boolean;
  onHide: () => void;
  conflictedItem: { id: string; name: string; count: number }; 
  onSuccess: (target: any) => void; 
  customOptions?: any[];
  type?: 'CLIENT' | 'SUB_DEPARTMENT' | 'TASK'; 
}

const ReassignmentModal = ({ show, onHide, conflictedItem, onSuccess, customOptions, type }: IReassignmentModalProps) => {
  const dispatch = useDispatch();
  const [targetOptions, setTargetOptions] = useState<any[]>([]);
  const [selectedTarget, setSelectedTarget] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const getEntityLabel = () => {
    if (type === 'TASK') return 'Task';
    if (type === 'CLIENT') return 'Client';
    return 'Sub-Department';
  };

  // Fetch Global List on Open
  useEffect(() => {
    if (show) {
      if (customOptions && type === 'CLIENT') {
            const opts = customOptions.filter((d: any) => d.value !== conflictedItem.id);
            setTargetOptions(opts);
            setIsLoadingList(false);
        } 
      else if (type === 'TASK') {
            setIsLoadingList(true);
            dispatch(actions.getAllTaskNamesGlobalAction((res: any) => {
                setIsLoadingList(false);
                if(res?.data) {
                    const opts = res.data
                        .filter((d: any) => d._id !== conflictedItem.id) 
                        .map((d: any) => ({
                            value: d._id,
                            label: `${d.name} (${d.activityTypeId?.name || 'Unknown'})`
                        }));
                    setTargetOptions(opts);
                }
            }));
      }
      else {
        setIsLoadingList(true);
        dispatch(actions.getAllSubDepartmentsGlobalAction((res: any) => {
            setIsLoadingList(false);
            if(res?.data) {
                const opts = res.data
                    .filter((d: any) => d._id !== conflictedItem.id)
                    .map((d: any) => ({
                        value: d._id,
                        label: `${d.subDepartmentName} (${d.departmentRef?.departmentName || 'Unknown Dept'})`
                    }));
                setTargetOptions(opts);
            }
        }));
      }
    }
  }, [show, customOptions, conflictedItem.id, dispatch, type]);

  const handleConfirm = () => {
    if (!selectedTarget) {
      showErrorToastMessage("Please select a target.");
      return;
    }

    setIsSubmitting(true);

    // --- LOGIC SWITCH ---
    if (type === 'TASK') {
        // CASE 1: HRBP TASK
        dispatch(actions.deleteTaskNameAction(conflictedItem.id, selectedTarget.value, (res: any) => {
            handleResponse(res);
        }));
    } 
    else if (type === 'CLIENT' || (customOptions && !type)) { 
        // CASE 2: CLIENTS
        dispatch(actions.deleteClientAction(conflictedItem.id, selectedTarget.value, (res: any) => {
            handleResponse(res);
        }) as any);
    } 
    else {
        // CASE 3: SUB-DEPARTMENTS (Default)
        dispatch(actions.deleteSubDepartmentAction(conflictedItem.id, selectedTarget.value, (res: any) => {
            handleResponse(res);
        }));
    }
  };

  // Helper to handle the response for all types
  const handleResponse = (res: any) => {
      setIsSubmitting(false);
      
      // Check success
      const isSuccess = 
        res?.success || 
        res?.code === 200 || 
        res?.data?.success || 
        (typeof res?.data === 'string' && res.data.includes("deleted"));

      if (isSuccess) {
          showSuccessToastMessage(`Reassigned and deleted successfully.`);
          onSuccess(selectedTarget); 
          onHide();
      } else {
          showErrorToastMessage(res?.error?.error_description || "Operation failed.");
      }
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" contentClassName="shadow-lg border-0">
      <Modal.Header closeButton>
        <Modal.Title className="text-danger">Action Required</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          You cannot delete <strong>{conflictedItem.name}</strong> because it has 
          <strong> {conflictedItem.count} records</strong> assigned.
        </p>
        <p>Please reassign them to another {getEntityLabel()}:</p>
        
        <Form.Group>
          <Form.Label>Move to:</Form.Label>
          <Select 
            options={targetOptions}
            onChange={setSelectedTarget}
            isLoading={isLoadingList}
            placeholder="Select target..."
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>Cancel</Button>
        <Button variant="danger" onClick={handleConfirm} disabled={isSubmitting || !selectedTarget}>
          {isSubmitting ? <Spinner size="sm" animation="border" /> : "Move & Delete"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ReassignmentModal;